/**
 * Cart pricing math — extracted verbatim from components/CartView.js.
 *
 * These functions are pure (no React, no atoms) but used to live inside a component
 * module, so anything that needed them had to import UI Kitten and the whole
 * component tree with them. The sales import (utils/salesImport/buildSaleInfo) must
 * price an imported order with the EXACT same math the checkout screen uses —
 * re-deriving it would let the two drift, and a drift here is wrong revenue in the
 * books. So the math moved here and CartView re-exports it; every existing import
 * site keeps working unchanged.
 */

import { getProductPrice } from './priceTypeHelpers'
import { resolveBonusLevel } from './bonusLevel'
import { parseDecimalAmount } from './decimalInput'

/**
 * These functions predate TypeScript in this repo and operate on raw product/client
 * documents, whose shape differs between the Firestore and Supabase read modes. The
 * JSDoc below types what they actually depend on — enough for TypeScript callers to be
 * checked properly, without pretending the whole document shape is known.
 *
 * @typedef {Object} PriceModificator
 * @property {number} value percentage added to the line price
 *
 * @typedef {Object} PriceType
 * @property {string} id
 *
 * @typedef {Object} CartItem
 * @property {number} quantity
 * @property {Object.<string, number>} [salePrices]
 * @property {number} [salePrice]
 * @property {number} [price]
 * @property {number} [purchasePrice]
 * @property {number} [packagePrice]
 * @property {boolean} [punchCardEnabled]
 * @property {{ percentage?: number, amount?: number }} [itemDiscount]
 *
 * @typedef {Object} LoyaltyClient
 * @property {number} [bonusValue]
 * @property {number} [punchCardValue]
 */

/**
 * @param {CartItem[]|null|undefined} cartItems
 * @param {string} store
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const totalCartPrice = (cartItems, store, priceType = null, priceTypes = null) => {
  if (!cartItems || !Array.isArray(cartItems)) {
    return 0
  }
  var totalPrice = 0
  for (let value of cartItems) {
    totalPrice = totalPrice + itemPriceWithoutModifiers(value, store, 0, priceType, priceTypes)
  }
  return totalPrice
}

/**
 * @param {CartItem[]|null|undefined} cartItems
 * @param {string} store
 * @param {PriceModificator[]|null|undefined} priceModificators
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const totalCartPriceWithPriceModificators = (cartItems, store, priceModificators, priceType = null, priceTypes = null) => {
  return totalCartPrice(cartItems, store, priceType, priceTypes) + priceModificatorsValue(cartItems, store, priceModificators, priceType, priceTypes)
}

/**
 * @param {CartItem[]|null|undefined} cartItems
 * @returns {number}
 */
export const totalCartPurchasePrice = (cartItems) => {
  if (!cartItems || !Array.isArray(cartItems)) {
    return 0
  }
  var totalPrice = 0
  for (let value of cartItems) {
    totalPrice = totalPrice + (((value.purchasePrice || 0) + (value.packagePrice || 0)) * value.quantity)
  }
  return totalPrice
}

/**
 * ATL-867 — turn a money TEXT INPUT into the number that may be persisted.
 *
 * `discountAmount` (and the other money fields on the checkout screen) live in React state as
 * whatever the cashier typed: `onChangeText` stores a STRING, while the initial/reset value is the
 * number 0. Everything that only DISPLAYS the value hides that by calling `parseFloat` at the point
 * of use, so the split state went unnoticed until a write path passed the raw value through:
 * `savedCarts.create` sent `discountAmount: "500"`, the backend `@IsNumber()` refused it with a 400,
 * and a 400 is a DETERMINISTIC dual-write defect — never queued, never retried, and Firestore had
 * already committed. The cart existed in one store and not the other, permanently.
 *
 * The contract is deliberately three-valued rather than "coerce and hope":
 *   - blank (''/null/undefined) → 0. An empty discount box means no discount; that is what every
 *     reader of this field already assumes (`discountPrice`'s own default, `SavedCartsModal`'s `?? 0`).
 *   - a parseable number (including a numeric string) → that number.
 *   - anything else → `null`, meaning UNPARSEABLE. It is not this function's job to invent a number
 *     for a money field; the caller must refuse the write and tell the cashier. Returning 0 here
 *     would be the exact `|| fallback` that silently books a wrong amount.
 *
 * Infinity is rejected along with NaN: the backend's `@IsNumber()` refuses both, so a value this
 * function accepted but the API would not would just move the deterministic loss somewhere else.
 *
 * ATL-1371 — the READ of a non-blank value is delegated to `parseDecimalAmount`, so this function
 * and every other monetary field in the app now agree on what a number is. The delegation also
 * closed a hole this function's own docstring forbade but its implementation had: it read the
 * non-blank case with `parseFloat`, for which '12abc' is 12, '1.2.3' is 1.2 and '1e5' is 100000 —
 * three invented discounts, none of them NaN, none of them caught by the `Number.isFinite` guard.
 * Only the BLANK contract is special to this function and stays here: an empty discount box means
 * no discount, which is 0.
 *
 * @param {number|string|null|undefined} value raw text-input / state value
 * @returns {number|null} a finite number, or null when the input cannot be read as one
 */
export const parseMoneyInput = (value) => {
  if (value === null || value === undefined) return 0
  if (typeof value === 'string' && value.trim() === '') return 0
  // The discount input already rewrites ',' → '.' on change, but a value restored from a saved cart
  // (or an imported document) may not have been through it — `parseDecimalAmount` handles both.
  return parseDecimalAmount(value)
}

/**
 * @param {number} [discountPercentage]
 * @param {number|string} [discountAmount]
 * @param {CartItem[]|null|undefined} cartItems
 * @param {string} store
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const discountPrice = (discountPercentage=0, discountAmount=0, cartItems, store, priceType = null, priceTypes = null) => {
  return (totalCartPrice(cartItems, store, priceType, priceTypes) * (discountPercentage / 100) + parseFloat(discountAmount))
}

/**
 * @param {CartItem[]|null|undefined} cartItems
 * @param {string} store
 * @param {PriceModificator[]|null|undefined} priceModificators
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const priceModificatorsValue = (cartItems, store, priceModificators, priceType = null, priceTypes = null) => {
  if (!priceModificators || !Array.isArray(priceModificators)) return 0
  if (!cartItems || !Array.isArray(cartItems)) {
    return 0
  }
  var totalPrice = totalCartPrice(cartItems, store, priceType, priceTypes)
  var priceModificatorsValue = 0
  priceModificators.forEach(value => {
    priceModificatorsValue += totalPrice * (value.value/100)
  })
  return priceModificatorsValue
}

/**
 * @param {LoyaltyClient|null} client
 * @param {number|null} bonusAction
 * @param {number} discountPercentage
 * @param {number|string} discountAmount
 * @param {CartItem[]|null|undefined} cartItems
 * @param {string} store
 * @param {boolean} cashbackEnabled
 * @param {PriceModificator[]|null|undefined} priceModificators
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const bonusReductionValue = (client, bonusAction, discountPercentage, discountAmount, cartItems, store, cashbackEnabled, priceModificators, priceType = null, priceTypes = null) => {
  if (!client) return 0
  if (!cashbackEnabled) return 0
  if (!bonusAction || bonusAction == 0) return 0
  if (!cartItems || !Array.isArray(cartItems)) return 0
  let availableBonus = (client.bonusValue || 0)
  let cartValue = totalCartPriceWithPriceModificators(cartItems, store, priceModificators, priceType, priceTypes) - discountPrice(discountPercentage, discountAmount, cartItems, store, priceType, priceTypes)
  if (availableBonus > cartValue) {
    return cartValue
  } else {
    return availableBonus
  }
}

/**
 * @param {LoyaltyClient|null} client
 * @param {number|null} bonusAction
 * @param {number} discountPercentage
 * @param {number|string} discountAmount
 * @param {CartItem[]|null|undefined} cartItems
 * @param {string} store
 * @param {boolean} cashbackEnabled
 * @param {PriceModificator[]|null|undefined} priceModificators
 * @param {boolean} [punchCardEnabled]
 * @param {number} [punchCardCapacity]
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const bonusAccumulationValue = (client, bonusAction, discountPercentage, discountAmount, cartItems, store, cashbackEnabled, priceModificators, punchCardEnabled = false, punchCardCapacity = 0, priceType = null, priceTypes = null) => {
  if (!client) return 0
  if (!cashbackEnabled) return 0
  if (bonusAction == null) return 0
  if (!cartItems || !Array.isArray(cartItems)) return 0
  return (finalTotalCartPrice(discountPercentage, discountAmount, cartItems, store, client, bonusAction, cashbackEnabled, punchCardEnabled, punchCardCapacity, priceModificators, priceType, priceTypes) * resolveBonusLevel(client) / 100)
  // return parseInt((finalTotalCartPrice(discount, cartItems, store, client, bonusAction) * (10) / 100)?.toFixed(0))
}

/**
 * @param {LoyaltyClient|null} client
 * @param {number} discountPercentage
 * @param {number|string} discountAmount
 * @param {CartItem[]|null|undefined} cartItems
 * @param {string} store
 * @param {boolean} punchCardEnabled
 * @param {number} punchCardCapacity
 * @param {PriceModificator[]|null|undefined} priceModificators
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const punchCardReduction = (client, discountPercentage, discountAmount, cartItems, store, punchCardEnabled, punchCardCapacity, priceModificators, priceType = null, priceTypes = null) => {
  if (!client) return 0
  if (!punchCardEnabled) return 0
  if (!cartItems || !Array.isArray(cartItems)) return 0
  if (client.punchCardValue >= (punchCardCapacity-1)) {
    // find cheapest cartitem that is part of punchcard and
    let cheapestCI = 0
    cartItems.forEach(value => {
      const itemPrice = getProductPrice(value, store, priceType, null, priceTypes)
      if (value.punchCardEnabled && (itemPrice < cheapestCI || cheapestCI === 0)) {
        // Ensure quantity is valid before division to prevent NaN
        const quantity = value.quantity || 1
        const itemPricePerUnit = finalItemPrice(value, store, discountPercentage, priceModificators, priceType, priceTypes) / quantity

        if (!isNaN(itemPricePerUnit)) {
          cheapestCI = itemPricePerUnit
        }
      }
    })

    return cheapestCI
  } else {
    return 0
  }
}

/**
 * @param {number} [discountPercentage]
 * @param {number|string} [discountAmount]
 * @param {CartItem[]|null|undefined} cartItems
 * @param {string} store
 * @param {LoyaltyClient|null} [client]
 * @param {number|null} [bonusAction]
 * @param {boolean} [cashbackEnabled]
 * @param {boolean} [punchCardEnabled]
 * @param {number} [punchCardCapacity]
 * @param {PriceModificator[]|null|undefined} [priceModificators]
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const finalTotalCartPrice = (discountPercentage=0, discountAmount=0, cartItems, store, client=null, bonusAction=null, cashbackEnabled=false, punchCardEnabled=false, punchCardCapacity, priceModificators, priceType = null, priceTypes = null) => {
  const totalPrice = totalCartPrice(cartItems, store, priceType, priceTypes)
  const modificatorsValue = priceModificatorsValue(cartItems, store, priceModificators, priceType, priceTypes)
  const discountValue = discountPrice(discountPercentage, discountAmount, cartItems, store, priceType, priceTypes)
  const bonusReduction = bonusReductionValue(client, bonusAction, discountPercentage, discountAmount, cartItems, store, cashbackEnabled, priceModificators, priceType, priceTypes)
  const punchCardReductionValue = punchCardReduction(client, discountPercentage, discountAmount, cartItems, store, punchCardEnabled, punchCardCapacity, priceModificators, priceType, priceTypes)

  const finalPrice = totalPrice + modificatorsValue - discountValue - bonusReduction - punchCardReductionValue

  // Return 0 if any calculation resulted in NaN
  return isNaN(finalPrice) ? 0 : finalPrice
}

/**
 * @param {CartItem} value
 * @param {string} store
 * @param {number} discountPercentage
 * @param {PriceModificator[]|null|undefined} priceModificators
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const finalItemPrice = (value, store, discountPercentage, priceModificators, priceType = null, priceTypes = null) => {
  // Use getProductPrice to support multiple price types
  const storedPrice = value.salePrices?.[store] ?? value.salePrice ?? value.price ?? null
  const priceFromType = getProductPrice(value, store, priceType, null, priceTypes)
  const basePrice = priceType ? (priceFromType || storedPrice || 0) : (storedPrice ?? priceFromType ?? 0)
  const quantity = value.quantity || 1 // Ensure quantity is valid
  var originalPrice = basePrice * quantity
  var price = originalPrice
  if (value.itemDiscount != null) {
    price = originalPrice * (100-value.itemDiscount?.percentage) / 100
  }
  if (priceModificators && Array.isArray(priceModificators)) {
    var priceModificatorsValue = 0
    priceModificators.forEach(modificator => {
      priceModificatorsValue += originalPrice * (modificator.value/100)
    })
    price = price + priceModificatorsValue
  }
  price = price * (100-(discountPercentage || 0)) / 100

  // Return 0 if calculation resulted in NaN
  return isNaN(price) ? 0 : price
}

/**
 * @param {CartItem} value
 * @param {string} store
 * @param {number} discount
 * @param {string|null} [priceType]
 * @param {PriceType[]|null} [priceTypes]
 * @returns {number}
 */
export const itemPriceWithoutModifiers = (value, store, discount, priceType = null, priceTypes = null) => {
  // Use getProductPrice to support multiple price types
  const storedPrice = value.salePrices?.[store] ?? value.salePrice ?? value.price ?? null
  const priceFromType = getProductPrice(value, store, priceType, null, priceTypes)
  const basePrice = priceType ? (priceFromType || storedPrice || 0) : (storedPrice ?? priceFromType ?? 0)
  var originalPrice = basePrice * value.quantity
  var price = originalPrice
  if (value.itemDiscount != null) {
    price = originalPrice * (100-(value.itemDiscount?.percentage || value.itemDiscount?.amount || 0)) / 100
  }
  price = price * (100-discount) / 100
  return price
}

/**
 * @param {CartItem[]|null|undefined} cartItems
 * @returns {number}
 */
export const totalCartQuantity = (cartItems) => {
  if (!cartItems || !Array.isArray(cartItems)) {
    return 0
  }
  return cartItems.reduce((acc, item) => acc + item.quantity, 0)
}
